import type { PropositionDetails, PropositionDocument } from "@dune/types";
import { Button, DataTable } from "@dune/ui";
import { Upload } from "lucide-react";
import React, { useMemo, useState, useCallback } from "react";
import { getDocumentUrl } from "../../../../api/queries/files.queries";
import { usePropositionDocuments } from "../../../../api/queries/propositions.queries";
import { useDeletePropositionDocumentMutation } from "../../../../api/mutations/propositions.mutations";
import { useDocumentStore } from "../../../../stores/document.store";
import ConfirmationDialog from "../../../../component/common/ConfirmationDialog";
import PropositionUploadDocumentModal from "../modals/PropositionUploadDocumentModal";
import { propositionDocumentsColumns } from "./documentsColumns";
import { useMe } from "../../../../api/queries/users.queries";

interface DocumentsTabProps {
	proposition: PropositionDetails;
}

export const DocumentsTab: React.FC<DocumentsTabProps> = ({ proposition }) => {
	const {
		data: documents,
		isLoading,
		error,
	} = usePropositionDocuments(proposition.id);
	const selectDocument = useDocumentStore((state) => state.selectDocument);

	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
	const [loadingDocumentId, setLoadingDocumentId] = useState<string | null>(
		null,
	);
	const [downloadingDocumentId, setDownloadingDocumentId] = useState<
		string | null
	>(null);
	const [deletingDocumentId, setDeletingDocumentId] = useState<string | null>(
		null,
	);
	const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
		useState(false);
	const [documentToDelete, setDocumentToDelete] =
		useState<PropositionDocument | null>(null);

	const deleteDocumentMutation = useDeletePropositionDocumentMutation(
		proposition.id,
		{
			onMutate: (documentId) => {
				setDeletingDocumentId(documentId);
			},
			onSettled: () => {
				setDeletingDocumentId(null);
			},
		},
	);

	const handleDocumentClick = useCallback(
		async (document: PropositionDocument) => {
			const docId = document.id.toString();
			if (loadingDocumentId === docId) return;

			if (!document.file.key || document.file.key.trim() === "") {
				return;
			}

			setLoadingDocumentId(docId);

			try {
				const url = await getDocumentUrl({
					bucket: "private",
					key: document.file.key,
				});

				if (url) {
					selectDocument(
						url,
						document.file.id,
						document.file.mimeType,
						document.file.name,
					);
				}
			} catch (error) {
				console.error(
					"Erreur lors de la récupération de l'URL du document:",
					error,
				);
			} finally {
				setLoadingDocumentId(null);
			}
		},
		[loadingDocumentId, selectDocument],
	);

	const handleDeleteClick = useCallback(
		(documentId: string) => {
			const document = documents?.find(
				(doc) => doc.id.toString() === documentId,
			);
			if (document) {
				setDocumentToDelete(document);
				setIsDeleteConfirmationOpen(true);
			}
		},
		[documents],
	);

	const handleConfirmDelete = useCallback(async () => {
		if (documentToDelete) {
			await deleteDocumentMutation.mutateAsync(documentToDelete.id.toString());
			setIsDeleteConfirmationOpen(false);
			setDocumentToDelete(null);
		}
	}, [documentToDelete, deleteDocumentMutation]);

	const documentsData = useMemo(() => {
		return documents || [];
	}, [documents]);

	const { me } = useMe();
	const isDuneUser = me?.role === "DUNE";
	const columns = propositionDocumentsColumns({
		loadingDocumentId,
		downloadingDocumentId,
		setDownloadingDocumentId,
		onDelete: isDuneUser ? handleDeleteClick : undefined,
		deletingDocumentId,
		isDuneUser,
	});

	if (error) return <div>Erreur lors du chargement des documents.</div>;

	return (
		<div className="h-full">
			<div className="flex items-center justify-between mb-2 w-full">
				<h2 className="text-lg text-text-main-blue font-semibold">Documents</h2>
				<Button
					onClick={() => setIsUploadModalOpen(true)}
					size="sm"
					className="flex items-center gap-2"
				>
					<Upload size={16} />
					Ajouter un document
				</Button>
			</div>
			<DataTable
				data={documentsData}
				columns={columns}
				noResultsText="Aucun document trouvé"
				isLoading={isLoading}
				onRowClick={handleDocumentClick}
				variant="static-clean"
			/>
			<PropositionUploadDocumentModal
				open={isUploadModalOpen}
				onOpenChange={setIsUploadModalOpen}
				propositionId={proposition.id}
			/>
			<ConfirmationDialog
				open={isDeleteConfirmationOpen}
				onOpenChange={setIsDeleteConfirmationOpen}
				title="Supprimer le document"
				description={`Êtes-vous sûr de vouloir supprimer le document "${documentToDelete?.file.name}" ? Cette action est irréversible.`}
				confirmText="Supprimer"
				cancelText="Annuler"
				variant="danger"
				onConfirm={handleConfirmDelete}
				isLoading={deleteDocumentMutation.isPending}
			/>
		</div>
	);
};
